import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Globe2 } from "lucide-react";

import { api } from "../api/client";
import type { Region, Site } from "../types";
import { getActiveHub, setActiveHub } from "../utils/hub";

/**
 * The admin panel's hub switcher. A hub is a site; picking one scopes every
 * admin page (dashboard, employees, teams, leave planner, performance,
 * documents, reports) to that hub's people — enforced server-side as an
 * intersection with the admin's existing site visibility, so it can only
 * narrow, never widen. "All hubs" restores the global view. The choice is
 * remembered on this device.
 */
export default function HubSwitcher() {
  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const active = getActiveHub();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get<Site[]>("/api/sites"),
          api.get<Region[]>("/api/regions").catch(() => [] as Region[]),
        ]);
        setSites(s);
        setRegions(r);
      } catch { /* switcher simply stays on All hubs */ }
    })();
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const activeSite = sites.find((s) => s.id === active) ?? null;

  const grouped = useMemo(() => {
    const byRegion = new Map<number | null, Site[]>();
    for (const s of sites) {
      const key = (s as { region_id?: number }).region_id ?? null;
      byRegion.set(key, [...(byRegion.get(key) ?? []), s]);
    }
    return Array.from(byRegion.entries())
      .map(([regionId, list]) => ({
        regionName: regions.find((r) => r.id === regionId)?.name ?? null,
        sites: list.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => (a.regionName ?? "").localeCompare(b.regionName ?? ""));
  }, [sites, regions]);

  const choose = (siteId: number | null) => {
    setActiveHub(siteId);
    setOpen(false);
    // A full reload is deliberate: every page refetches with the new scope,
    // so nothing on screen can be left showing the previous hub's data.
    window.location.reload();
  };

  // If the stored hub no longer exists (site deleted), fall back quietly.
  useEffect(() => {
    if (active != null && sites.length > 0 && !sites.some((s) => s.id === active)) {
      setActiveHub(null);
    }
  }, [active, sites]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Choose which hub the admin panel shows"
        className={[
          "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors",
          activeSite
            ? "border-pme-navy bg-pme-navy text-white hover:bg-pme-navy/90"
            : "border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200",
        ].join(" ")}
      >
        {activeSite ? (
          <Building2 size={13} strokeWidth={2} aria-hidden />
        ) : (
          <Globe2 size={13} strokeWidth={2} aria-hidden />
        )}
        <span className="max-w-[160px] truncate">{activeSite ? activeSite.name : "All hubs"}</span>
        <ChevronDown size={12} strokeWidth={2} aria-hidden className="opacity-70" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[62] mt-1 w-72 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          <p className="px-2 pb-1.5 pt-0.5 text-[10px] leading-snug text-gray-400">
            Scope the whole admin panel to one hub. You only ever see hubs you
            already have visibility of.
          </p>
          <button
            type="button"
            onClick={() => choose(null)}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-gray-50"
          >
            <Globe2 size={14} strokeWidth={1.75} aria-hidden className="text-pme-blue" />
            <span className="flex-1 font-medium text-gray-900">All hubs</span>
            {active == null && <Check size={14} strokeWidth={2} aria-hidden className="text-pme-blue" />}
          </button>
          <div className="my-1 border-t border-gray-100" />
          <div className="max-h-64 overflow-y-auto">
            {grouped.map((g) => (
              <div key={g.regionName ?? "other"}>
                {g.regionName && (
                  <p className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {g.regionName}
                  </p>
                )}
                {g.sites.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => choose(s.id)}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <Building2 size={14} strokeWidth={1.75} aria-hidden className="text-gray-400" />
                    <span className="flex-1 truncate text-gray-900">{s.name}</span>
                    {active === s.id && <Check size={14} strokeWidth={2} aria-hidden className="text-pme-blue" />}
                  </button>
                ))}
              </div>
            ))}
            {sites.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-gray-400">No sites found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
